import React, { useState } from 'react';
import { Percent, Copy, Trash2 } from 'lucide-react';

const PercentageCalculatorTool = () => {
  const [mode, setMode] = useState('whatIsXPercent'); 
  const [value1, setValue1] = useState('');
  const [value2, setValue2] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const num1 = parseFloat(value1);
    const num2 = parseFloat(value2);

    if (isNaN(num1) || isNaN(num2)) {
      setResult(null);
      return;
    }

    let answer;
    let explanation;

    switch (mode) {
      case 'whatIsXPercent':
        // What is X% of Y?
        answer = (num1 / 100) * num2;
        explanation = `${num1}% of ${num2} = ${answer.toFixed(4)}`;
        break;
      case 'xIsWhatPercent':
        // X is what percent of Y?
        answer = (num1 / num2) * 100;
        explanation = `${num1} is ${answer.toFixed(4)}% of ${num2}`;
        break;
      case 'percentChange':
        // Percent change from X to Y
        answer = ((num2 - num1) / Math.abs(num1)) * 100;
        explanation = `Change from ${num1} to ${num2} = ${answer >= 0 ? '+' : ''}${answer.toFixed(4)}%`;
        break;
      case 'increaseBy':
        // Increase X by Y%
        answer = num1 * (1 + num2 / 100);
        explanation = `${num1} increased by ${num2}% = ${answer.toFixed(4)}`;
        break;
      case 'decreaseBy':
        // Decrease X by Y%
        answer = num1 * (1 - num2 / 100);
        explanation = `${num1} decreased by ${num2}% = ${answer.toFixed(4)}`;
        break;
      default:
        return;
    }

    setResult({ answer, explanation });
  };

  const copyResult = () => {
    if (result) {
      navigator.clipboard.writeText(result.answer.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const modeLabels = {
    whatIsXPercent: { title: 'What is X% of Y?', label1: 'Percentage (%)', label2: 'Number' },
    xIsWhatPercent: { title: 'X is what % of Y?', label1: 'Number', label2: 'Total' },
    percentChange: { title: 'Percent Change', label1: 'From', label2: 'To' },
    increaseBy: { title: 'Increase by %', label1: 'Original Number', label2: 'Increase by (%)' },
    decreaseBy: { title: 'Decrease by %', label1: 'Original Number', label2: 'Decrease by (%)' }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(modeLabels).map(([key, info]) => (
          <button
            key={key}
            onClick={() => { setMode(key); setResult(null); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {info.title}
          </button>
        ))}
      </div>

      <div className="p-4 bg-gray-800 rounded-lg">
        <h3 className="text-lg font-medium text-white mb-4">{modeLabels[mode].title}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {modeLabels[mode].label1}
            </label>
            <input
              type="number"
              value={value1}
              onChange={(e) => setValue1(e.target.value)}
              placeholder="Enter number"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && calculate()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {modeLabels[mode].label2}
            </label>
            <input
              type="number"
              value={value2}
              onChange={(e) => setValue2(e.target.value)}
              placeholder="Enter number"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && calculate()}
            />
          </div>
        </div>
      </div>

      <button
        onClick={calculate}
        disabled={!value1 || !value2}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <Percent size={18} />
        Calculate
      </button>

      {result && (
        <div className="p-4 bg-gray-800 rounded-lg">
          <div className="text-sm text-gray-400 mb-2">{result.explanation}</div>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-green-400">
              {result.answer.toFixed(4).replace(/\.?0+$/, '')}
            </div>
            <button
              onClick={copyResult}
              className={`p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={18} />
            </button>
          </div>
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Quick Reference</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Common Percentages</div>
            <div>10% = 1/10 • 25% = 1/4 • 50% = 1/2</div>
            <div>33.33% ≈ 1/3 • 66.67% ≈ 2/3 • 75% = 3/4</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Formulas</div>
            <div>X% of Y = (X/100) × Y</div>
            <div>X is what % of Y = (X/Y) × 100</div>
          </div>
        </div>
      </div>

      <button
        onClick={() => { setValue1(''); setValue2(''); setResult(null); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default PercentageCalculatorTool;
